import { useEquipmentDetailsRouteState } from '@/features/equipment-browser/equipment-details/hooks/use-equipment-details-route-state';
import { isEquipmentGroupId } from '@/features/equipment-browser/equipment-groups/equipment-groups.utils';
import { useGetEquipmentBrowserConfig } from '@/hooks/query-hooks/gaia-api/equipment-browser';
import { useCurrentFacility } from '@/hooks/use-current-facility';

/**
 * Effective equipment browser view: explicit route view, else config default. A
 * client-side group (Faulted, Manual devices) resolves to the default view, whose
 * devices it narrows, so a group id never reaches the BFF.
 */
export const resolveEquipmentViewId = (
  currentViewId: string | undefined,
  defaultView: string | undefined
): string | undefined => {
  if (isEquipmentGroupId(currentViewId)) return defaultView;
  return currentViewId ?? defaultView;
};

/** Route view id when set; otherwise equipment browser default view from config. */
export const useResolvedEquipmentViewId = (): string | undefined => {
  const { viewId } = useEquipmentDetailsRouteState();
  const { agentId } = useCurrentFacility();
  const { data: ebConfig } = useGetEquipmentBrowserConfig({ agentId });

  return resolveEquipmentViewId(viewId, ebConfig?.defaultView);
};

/**
 * The view id the page is showing, for keys and selection: a client-side group's own
 * id (Faulted, Manual devices), else the resolved view. Use it for the selected tab,
 * filter keys and section keys; use `resolveEquipmentViewId` for anything sent to the BFF.
 */
export const selectEquipmentViewId = (
  currentViewId: string | undefined,
  defaultView: string | undefined
): string | undefined => {
  if (isEquipmentGroupId(currentViewId)) return currentViewId;
  return resolveEquipmentViewId(currentViewId, defaultView);
};

/** Route group or view id when set; otherwise the default view. See `selectEquipmentViewId`. */
export const useSelectedEquipmentViewId = (): string | undefined => {
  const { viewId } = useEquipmentDetailsRouteState();
  const { agentId } = useCurrentFacility();
  const { data: ebConfig } = useGetEquipmentBrowserConfig({ agentId });

  return selectEquipmentViewId(viewId, ebConfig?.defaultView);
};
